import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { sessionSaveService } from '../services/sessionSaveService';

interface UseEditModeParams {
  tickerList: string[];
  listName: string;
  setTickerList: (list: string[]) => void;
  setListName: (name: string) => void;
  isEditingTickers: boolean;
  setIsEditingTickers: (editing: boolean) => void;
  returnPath: string;
  onSaveChanges?: (tempTickerList: string[], tempListName: string) => Promise<void> | void;
  hiddenStatistics?: {
    daily?: string[];
    technicals?: string[];
    fundamentals?: string[];
    analystViews?: string[];
  };
}

interface UseEditModeReturn {
  tempTickerList: string[];
  tempListName: string;
  setTempTickerList: (list: string[]) => void;
  setTempListName: (name: string) => void;
  handleStartEdit: () => void;
  handleCancelEdit: () => void;
  handleDoneEdit: () => Promise<void>;
  handleSave: () => void;
}

/**
 * Hook for managing edit mode state and handlers for List* pages
 */
export const useEditMode = ({
  tickerList,
  listName,
  setTickerList,
  setListName,
  isEditingTickers,
  setIsEditingTickers,
  returnPath,
  onSaveChanges,
  hiddenStatistics,
}: UseEditModeParams): UseEditModeReturn => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [tempTickerList, setTempTickerList] = useState<string[]>([]);
  const [tempListName, setTempListName] = useState<string>('');

  const handleStartEdit = useCallback(() => {
    setTempTickerList([...tickerList]);
    setTempListName(listName);
    setIsEditingTickers(true);
  }, [tickerList, listName, setIsEditingTickers]);

  const handleCancelEdit = useCallback(() => {
    // For ListAnalystViews: reset to current values
    // For other pages: clear temp values
    // Using the pattern that works for all - reset to current values
    setTempTickerList([...tickerList]);
    setTempListName(listName);
    setIsEditingTickers(false);
  }, [tickerList, listName, setIsEditingTickers]);

  const handleDoneEdit = useCallback(async () => {
    if (onSaveChanges) {
      // If custom save handler provided (like ListAnalystViews), use it
      // Pass temp values so the callback can use them
      await onSaveChanges(tempTickerList, tempListName);
    } else {
      // Default behavior: apply changes directly
      const newTickerList = [...tempTickerList];
      setTickerList(newTickerList);
      setListName(tempListName);
      setTempTickerList([]);
      setTempListName('');
      setIsEditingTickers(false);
    }
  }, [tempTickerList, tempListName, setTickerList, setListName, setIsEditingTickers, onSaveChanges]);

  const handleSave = useCallback(() => {
    if (!isAuthenticated) {
      // For unauthenticated users, always use the most current values:
      // - If in edit mode, use temp values (which may have edits)
      // - Otherwise, use current tickerList/listName (which includes newly added tickers)
      // - But also check sessionStorage as source of truth since it's updated immediately when tickers are added
      let tickersToSave: string[];
      let nameToSave: string;
      
      if (isEditingTickers && tempTickerList.length > 0) {
        // In edit mode, use temp values (edited values)
        tickersToSave = tempTickerList;
        nameToSave = tempListName || listName;
      } else {
        // Not in edit mode, use current state (includes newly added tickers)
        // Also check sessionStorage as it's updated immediately when tickers are added
        const savedTickers = sessionStorage.getItem('stocktable-ticker-list');
        if (savedTickers) {
          try {
            const parsedTickers = JSON.parse(savedTickers);
            // Use sessionStorage if it has more tickers (more up-to-date), otherwise use state
            tickersToSave = parsedTickers.length >= tickerList.length ? parsedTickers : tickerList;
          } catch (e) {
            tickersToSave = tickerList;
          }
        } else {
          tickersToSave = tickerList;
        }
        
        const savedName = sessionStorage.getItem('stocktable-list-name');
        nameToSave = savedName || listName;
      }
      
      // Explicitly save session data to sessionStorage before navigating
      // This ensures the data is saved even if the useEffect hasn't run yet
      console.log('💾 Saving session data before navigation:', {
        tickerList: tickersToSave,
        listName: nameToSave,
        tickerCount: tickersToSave.length,
        isEditingTickers,
        usingTempValues: isEditingTickers && tempTickerList.length > 0,
        source: isEditingTickers && tempTickerList.length > 0 ? 'temp' : 'state/sessionStorage'
      });
      
      // Save ticker list and list name to sessionStorage
      sessionStorage.setItem('stocktable-ticker-list', JSON.stringify(tickersToSave));
      sessionStorage.setItem('stocktable-list-name', nameToSave);
      
      // Save hidden statistics if provided
      if (hiddenStatistics) {
        // Merge with existing hidden statistics
        const existingHiddenStats = sessionStorage.getItem('stocktable-hidden-statistics');
        const existingObject = existingHiddenStats ? JSON.parse(existingHiddenStats) : {};
        const mergedObject = {
          ...existingObject,
          ...hiddenStatistics
        };
        sessionStorage.setItem('stocktable-hidden-statistics', JSON.stringify(mergedObject));
      }
      
      // Also update the session save service with current data
      sessionSaveService.registerSessionData({
        tickerList: tickersToSave,
        listName: nameToSave,
        hiddenStatistics: hiddenStatistics || {}
      });
      
      // Redirect to signup with save action context
      navigate('/signup', {
        state: {
          fromSave: true,
          returnPath,
        },
      });
    } else {
      // For authenticated users, trigger save changes
      if (onSaveChanges) {
        onSaveChanges(tempTickerList, tempListName);
      } else {
        handleDoneEdit();
      }
    }
  }, [isAuthenticated, navigate, returnPath, onSaveChanges, handleDoneEdit, tickerList, listName, hiddenStatistics, isEditingTickers, tempTickerList, tempListName]);

  return {
    tempTickerList,
    tempListName,
    setTempTickerList,
    setTempListName,
    handleStartEdit,
    handleCancelEdit,
    handleDoneEdit,
    handleSave,
  };
};

